/**
 * seatingMapUtils.js
 * Data model, default values, and helper functions for SeatingMapWidget.
 * Uses a flat "free rows" model — each row has an (x, y) position and is
 * draggable on the canvas.
 */
import { v4 as uuidv4 } from 'uuid';

// ─── Landmark labels & icons ──────────────────────────────────────────────────

export const LANDMARK_LABELS = {
  aronHaKodesh: 'ארון הקודש',
  bima: 'בימה',
  amudTefilla: 'עמוד תפילה',
  entrance: 'כניסה',
};

export const LANDMARK_ICONS = {
  aronHaKodesh: '📜',
  bima: '📖',
  amudTefilla: '🕯️',
  entrance: '🚪',
};

// ─── ID generators ────────────────────────────────────────────────────────────

export const generateSeatId = () => `seat-${uuidv4().slice(0, 8)}`;
export const generateRowId = () => `row-${uuidv4().slice(0, 8)}`;

// ─── Seat / Row generators ────────────────────────────────────────────────────

export const generateEmptySeats = (count) =>
  Array.from({ length: count }, () => ({
    id: generateSeatId(),
    occupant: '',
  }));

export const generateRow = (seatsCount = 6, x = 0, y = 0) => ({
  id: generateRowId(),
  x,
  y,
  seats: generateEmptySeats(seatsCount),
});

// ─── Constants ────────────────────────────────────────────────────────────────

export const DEFAULT_SEAT_SIZE = 0.9;
export const SEAT_GAP = 0.2;
const ROW_GAP = 1.4;

// ─── Migration from old columns model ─────────────────────────────────────────

/**
 * Convert old column-based mapData to the new flat-rows model.
 * If data already has `rows`, returns it unchanged.
 */
export const migrateMapData = (mapData) => {
  if (!mapData) return null;
  if (mapData.rows) return mapData;              // already new format
  if (!mapData.columns) return mapData;           // unknown format, pass through

  const seatSize = mapData.seatSize || DEFAULT_SEAT_SIZE;
  const seatGap = SEAT_GAP * (seatSize / DEFAULT_SEAT_SIZE);
  const rowGap = ROW_GAP * (seatSize / DEFAULT_SEAT_SIZE);
  const aisleWidth = mapData.aisleWidth || 2;
  const padding = 1.5;
  const columns = mapData.columns;
  const totalAisles = columns.length - 1;
  const availableWidth = (mapData.width || 40) - padding * 2 - totalAisles * aisleWidth;
  const columnWidth = availableWidth / columns.length;

  const aron = (mapData.landmarks || []).find(l => l.type === 'aronHaKodesh' && l.visible !== false);
  const autoStartY = aron ? aron.y + aron.height + 1 : 3;

  const rows = [];
  columns.forEach((col, colIndex) => {
    const columnX = padding + colIndex * (columnWidth + aisleWidth);
    const colStartY = col.startY != null ? col.startY : autoStartY;
    let currentY = colStartY;

    (col.rows || []).forEach(row => {
      const seatUnit = seatSize + seatGap;
      const rowWidth = row.seats.length * seatUnit - seatGap;
      const align = row.align || 'center';
      let offsetX;
      if (align === 'right') offsetX = columnX;
      else if (align === 'left') offsetX = columnX + columnWidth - rowWidth;
      else offsetX = columnX + (columnWidth - rowWidth) / 2;

      rows.push({
        id: row.id || generateRowId(),
        x: Math.round(offsetX * 2) / 2,
        y: Math.round(currentY * 2) / 2,
        seats: row.seats.map(s => ({ id: s.id, occupant: s.occupant || '' })),
      });
      currentY += rowGap + (row.gapAfter || 0);
    });
  });

  // Return new-format data, dropping old fields
  const { columns: _, layoutMode: __, freeformSeats: ___, aisleWidth: ____, ...rest } = mapData;
  return { ...rest, rows };
};

// ─── Default map data ─────────────────────────────────────────────────────────

export const createDefaultMapData = () => {
  const seatSize = DEFAULT_SEAT_SIZE;
  const seatGap = SEAT_GAP;
  const seatUnit = seatSize + seatGap;
  const rowGap = ROW_GAP;

  // Create 5 rows on each side (left & right of centre)
  const startY = 5;
  const leftX = 2;
  const rightX = 40 - 2 - 6 * seatUnit + seatGap;
  const rows = [];
  for (let i = 0; i < 5; i++) {
    rows.push(generateRow(6, leftX, startY + i * rowGap));
    rows.push(generateRow(6, rightX, startY + i * rowGap));
  }

  return {
    width: 40,
    height: 30,
    seatSize,

    landmarks: [
      { id: 'aron',     type: 'aronHaKodesh', x: 15, y: 0,   width: 10, height: 2,   visible: true },
      { id: 'bima',     type: 'bima',         x: 14, y: 13,  width: 12, height: 3,   visible: true },
      { id: 'amud',     type: 'amudTefilla',  x: 17, y: 3,   width: 6,  height: 1.5, visible: true },
      { id: 'entrance', type: 'entrance',     x: 15, y: 29,  width: 10, height: 1,   visible: true },
    ],

    rows,

    colors: {
      occupied: '#1e3a5f',
      empty: '#86efac',
      landmark: '#8a6d08',
    },
  };
};

// ─── Seat position calculation ────────────────────────────────────────────────

/**
 * Compute rendered positions for every seat across all rows.
 * Returns [{ id, occupant, x, y, rowId }]
 */
export const getAllSeatsWithPositions = (mapData) => {
  const { rows, seatSize = DEFAULT_SEAT_SIZE } = mapData;
  const scale = seatSize / DEFAULT_SEAT_SIZE;
  const seatGap = SEAT_GAP * scale;

  const seats = [];
  (rows || []).forEach(row => {
    row.seats.forEach((seat, i) => {
      seats.push({
        ...seat,
        x: row.x + i * (seatSize + seatGap),
        y: row.y,
        rowId: row.id,
      });
    });
  });
  return seats;
};

/**
 * Return { x, y, width, height } bounding box for a row (for drag overlay).
 */
export const getRowBounds = (row, seatSize, seatGap) => {
  const w = row.seats.length * (seatSize + seatGap) - seatGap;
  return { x: row.x, y: row.y, width: w, height: seatSize };
};

// ─── Counting helpers ─────────────────────────────────────────────────────────

export const countSeats = (mapData) =>
  (mapData.rows || []).reduce((sum, r) => sum + r.seats.length, 0);

export const countOccupied = (mapData) =>
  (mapData.rows || []).reduce(
    (sum, r) => sum + r.seats.filter(s => s.occupant).length, 0,
  );

// ─── Text helpers ─────────────────────────────────────────────────────────────

export const truncateName = (name, maxChars = 5) => {
  if (!name) return '';
  return name.length > maxChars ? name.slice(0, maxChars) + '…' : name;
};

// ─── Row CRUD ─────────────────────────────────────────────────────────────────

export const addRow = (mapData, seatsCount = 6) => {
  const { seatSize = DEFAULT_SEAT_SIZE } = mapData;
  const scale = seatSize / DEFAULT_SEAT_SIZE;
  const seatGap = SEAT_GAP * scale;
  const rowWidth = seatsCount * (seatSize + seatGap) - seatGap;
  const x = (mapData.width - rowWidth) / 2;
  const y = mapData.height / 2;
  const newRow = generateRow(seatsCount, Math.round(x * 2) / 2, Math.round(y * 2) / 2);
  return { ...mapData, rows: [...(mapData.rows || []), newRow] };
};

export const removeRow = (mapData, rowId) => {
  if ((mapData.rows || []).length <= 1) return mapData;
  return { ...mapData, rows: mapData.rows.filter(r => r.id !== rowId) };
};

export const duplicateRow = (mapData, rowId) => {
  const rows = mapData.rows || [];
  const idx = rows.findIndex(r => r.id === rowId);
  if (idx === -1) return mapData;
  const src = rows[idx];
  const scale = (mapData.seatSize || DEFAULT_SEAT_SIZE) / DEFAULT_SEAT_SIZE;
  const newRow = {
    id: generateRowId(),
    x: src.x,
    y: src.y + ROW_GAP * scale,
    seats: src.seats.map(s => ({ id: generateSeatId(), occupant: s.occupant })),
  };
  const next = [...rows];
  next.splice(idx + 1, 0, newRow);
  return { ...mapData, rows: next };
};

export const updateRowSeatCount = (mapData, rowId, newCount) => ({
  ...mapData,
  rows: (mapData.rows || []).map(row => {
    if (row.id !== rowId) return row;
    let seats;
    if (newCount > row.seats.length) {
      seats = [...row.seats, ...generateEmptySeats(newCount - row.seats.length)];
    } else {
      seats = row.seats.slice(0, newCount);
    }
    return { ...row, seats };
  }),
});

export const updateRowPosition = (mapData, rowId, x, y) => ({
  ...mapData,
  rows: (mapData.rows || []).map(r =>
    r.id === rowId ? { ...r, x, y } : r,
  ),
});

export const updateRowSeats = (mapData, rowId, seats) => ({
  ...mapData,
  rows: (mapData.rows || []).map(r =>
    r.id === rowId ? { ...r, seats } : r,
  ),
});

// ─── Landmark update ──────────────────────────────────────────────────────────

export const updateLandmark = (mapData, landmarkId, updates) => ({
  ...mapData,
  landmarks: mapData.landmarks.map(lm =>
    lm.id === landmarkId ? { ...lm, ...updates } : lm,
  ),
});
